import { Page } from '../../pages/page';
import { CommonResponse } from '../../shared/types/backend/pay-response.type';
import { getLang } from '../../utils/landing';
import { URL } from '../url/url';

export class Locale {
    private static _loadedPromise?: Promise<void>;

    public static readonly TRANSLATIONS = {
        en: `${URL.getStaticsUrl()}front/locale/en.json`,
        es: `${URL.getStaticsUrl()}front/locale/es.json`,
        pt: `${URL.getStaticsUrl()}front/locale/pt.json`,
        ca: `${URL.getStaticsUrl()}front/locale/ca.json`,
        pl: `${URL.getStaticsUrl()}front/locale/pl.json`,
    };

    private static updateTexts() {
        $('[data-i18n]:not([data-trans])')
            .i18n();

        // Translate customer defined texts
        const elements = $('[data-trans]')
            .toArray();

        for (const element of elements) {
            const transKey = element.dataset.trans;
            const defaultI18n = element.dataset.i18n;

            if (!transKey) continue;

            const match = /(?:\[(\w+)])?(.+)(?:\((.+)\))?/g.exec(transKey);
            const defaultMatch = /(?:\[(\w+)])?(.+)(?:\((.+)\))?/g.exec(defaultI18n ?? '');

            if (match === null) continue;

            const el = $(element);

            const [, type, key, args] = match;
            const [, defaultType, defaultKey, defaultArgs] = defaultMatch ?? [];

            const translation = Locale.getTrans(key, defaultKey, args)
                .toString();

            switch (type) {
            case undefined:
                el.text(translation);
                break;
            case 'html':
                el.html(translation);
                break;
            default:
                el.attr(type, translation);
            }
        }
    }

    public static getTrans(key: string, i18nKeyFallback?: string, ...args: any[]): string | string[] {
        const lang = Locale.getLang();
        const payResponse = Page.getPayResponse<CommonResponse>();

        const [sectionKey, translationKey] = key.split('.');

        /* @ts-ignore */
        const value = payResponse?.pay_configuration?.settings?.[sectionKey]?.text?.[lang]?.[translationKey];

        if (value) {
            return value.replace(
                /\$([0-9]+)/g,
                (_: any, p1: string) => args[Number(p1) - 1] ?? '',
            );
        }

        return i18nKeyFallback ? $.i18n(i18nKeyFallback, ...args) : '';
    }

    public static getDecimalSeparator(): string {
        return (1.1).toLocaleString()
            .substring(1, 2);
    }

    public static loadTranslations(): Promise<void> {
        // @ts-ignore
        $.i18n({
            locale: Locale.getLang(),
        });

        // @ts-ignore
        $.i18n.debug = false;

        // This prevents the promise from loading twice
        if (this._loadedPromise !== undefined) {
            return this._loadedPromise;
        }

        this._loadedPromise = new Promise<void>((resolve, reject) => {
            $.i18n()
                // @ts-ignore
                .load(Locale.TRANSLATIONS)
                .done(() => {
                    Locale.updateTexts();
                    resolve();
                })
                .fail(reject);
        });

        return this._loadedPromise;
    }

    /**
     * Checks if a given language is available, otherwise returns 'en' as a fallback.
     *
     * As a side note: This is important for paycomet because if the lang its invalid the form will stop working.
     */
    private static fallbackLang(lang: string | null): string {
        const fallback = 'en';

        if (lang === null) {
            return fallback;
        }

        return Locale.getAvailableLangs()
            .includes(lang) ? lang : fallback;
    }

    private static getAvailableLangs(): string[] {
        return Object.keys(Locale.TRANSLATIONS);
    }

    private static getUrlLang(): string | null {
        const currentURL = new window.URL(window.location.toString());
        return currentURL.searchParams.get('lang');
    }

    public static getNavigatorLanguage() {
        return navigator.language.split('-')[0];
    }

    /**
     * Gets the alpha2 from the lang url parameter, otherwise parses the navigator lang.
     */
    public static getLang(): string {
        const payResponse = Page.getPayResponse<CommonResponse>();

        if (payResponse?.pay_configuration?.settings?.configuration?.supported_languages) {
            return getLang(payResponse?.pay_configuration?.settings?.configuration?.supported_languages);
        }

        const urlLang = Locale.getUrlLang();

        if (urlLang !== null) {
            return Locale.fallbackLang(urlLang);
        }

        return Locale.fallbackLang(
            Locale.getNavigatorLanguage(),
        );
    }
}
